// The Dashboard "Needs attention" card (docs/05 proactive v1 + v2). Renders only when
// the on-the-fly GET /v1/attention returns something, so a healthy fleet shows nothing.
// Items are rendered GENERICALLY — the card never switches on a closed set of kinds, so
// new server-side checks (uptime_drop, unplayed_media, long_broadcast, and future ones)
// appear with no client change: text-first, a severity mark, an optional deep-link, and
// the "Ask assistant" affordance that opens the panel pre-loaded with a diagnosis prompt.

import { Link } from '@tanstack/react-router';
import { useAttention, type AttentionItem } from '../api/attention.ts';
import { useAssistantContext } from '../assistant/AssistantContext.tsx';
import '../assistant/assistant-s6.css';

/** The item title, wrapped in a deep-link when the item names a page we can route
 *  to; otherwise plain text (some kinds — e.g. a long-running broadcast — have no
 *  single target). Unknown pages fall through to plain text, never a broken link. */
function ItemTitle({ item }: { item: AttentionItem }) {
  const link = item.link;
  if (link) {
    if (link.page === 'screens')
      return <Link to="/screens" search={{ screen: link.id }} className="cell-strong">{item.title}</Link>;
    if (link.page === 'schedules')
      return <Link to="/schedules" className="cell-strong">{item.title}</Link>;
    if (link.page === 'playlists')
      return <Link to="/playlists" className="cell-strong">{item.title}</Link>;
    if (link.page === 'analytics')
      return <Link to="/analytics" className="cell-strong">{item.title}</Link>;
  }
  return <span className="cell-strong">{item.title}</span>;
}

export function NeedsAttentionCard() {
  const attention = useAttention();
  const { ask } = useAssistantContext();
  const items = attention.data ?? [];

  // Only render when there is something to act on.
  if (items.length === 0) return null;

  return (
    <div className="card card-pad mb-4 attention-card">
      <div className="card-title">Needs attention</div>
      <ul className="attention-list">
        {items.map((item) => (
          <li key={item.id} className={`attention-item sev-${item.severity}`}>
            <span className="attention-dot" aria-hidden />
            <div className="attention-body">
              <ItemTitle item={item} />
              {item.detail && <div className="cell-dim">{item.detail}</div>}
            </div>
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => ask(item.ask)}
              aria-label={`Ask the assistant about: ${item.title}`}
            >
              Ask assistant
            </button>
          </li>
        ))}
      </ul>
      <p className="hint attention-slack-hint">
        Want these in Slack? <Link to="/settings">Add the “Attention” metric</Link> to a scheduled
        report.
      </p>
    </div>
  );
}
